import { API, Auth } from "aws-amplify";
import { DataStore } from "@aws-amplify/datastore"; // Still used for READS
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { Game, GameSeries } from "../models";
import { updateGameInDB } from "../services/datastoreService";
import { persistLocalStateToCloud } from "./playerState";
import { createGame as createGameMutation } from "../graphql/mutations";
import { getGame } from "../graphql/queries";

interface SeriesType {
  id: string;
  games: Game[];
  gameIds: string[];
  player1: string;
  player2: string;
  player1Wins: number;
  player2Wins: number;
}

/**
 * Migrate old "myGames" key to new "gameList" key for cloud sync
 * This is a one-time migration
 */
function migrateOldGamesList() {
  const oldKey = "myGames";
  const newKey = "gameList";

  const oldData = localStorage.getItem(oldKey);
  const newData = localStorage.getItem(newKey);

  // If old data exists but new doesn't, migrate
  if (oldData && !newData) {
    console.log("🔄 Migrating game list from 'myGames' to 'gameList'");
    localStorage.setItem(newKey, oldData);
    // Keep old data for now (don't delete for safety)
  }

  // If both exist, merge them
  if (oldData && newData) {
    try {
      const oldGames: string[] = JSON.parse(oldData);
      const newGames: string[] = JSON.parse(newData);
      const merged = Array.from(new Set([...oldGames, ...newGames]));
      localStorage.setItem(newKey, JSON.stringify(merged));
      console.log("🔄 Merged old and new game lists");
    } catch (e) {
      console.error("Failed to merge game lists:", e);
    }
  }
}

export function getMyGamesFromLocalStorage() {
  // Run migration check on first call
  migrateOldGamesList();

  let returnArray: string[] = [];
  // Use "gameList" key for cloud sync compatibility
  let myGamesStr = localStorage.getItem("gameList");
  if (myGamesStr) {
    let myGames: string[] = JSON.parse(myGamesStr);
    if (myGames.length > 0) {
      returnArray = [...myGames];
    }
  }

  let mySeriesStr = localStorage.getItem("mySeries");
  if (mySeriesStr) {
    let mySeries: SeriesType[] = JSON.parse(mySeriesStr);
    if (mySeries.length > 0) {
      //   let mySeriesGameIds = mySeries.map((s) => s.games.map((g) => g.id));
      // Filter out null/undefined series and those without gameIds
      let mySeriesGameIds = mySeries
        .filter((s) => s && s.gameIds && Array.isArray(s.gameIds))
        .map((s) => s.gameIds)
        .flat();
      returnArray = [...returnArray, ...mySeriesGameIds];
    }
  }
  return returnArray;
}

async function updateSeries(game: Game) {
  console.log("update Series: ", game);

  if (game.seriesId) {
    const original = await DataStore.query(GameSeries, game.seriesId!);

    let player1Wins = original?.player1Wins || 0;
    let player2Wins = original?.player2Wins || 0;
    if (game.winner === original?.player1) player1Wins++;
    if (game.winner === original?.player2) player2Wins++;
    //   const updateSeries: Partial<GameSeries> = {};
    console.log("🚀 ~ original series", original);
    const update = await DataStore.save(
      GameSeries.copyOf(original!, (updateSeries) => {
        updateSeries.gameIds = [...original?.gameIds!, game.id];
        updateSeries.player1Wins = player1Wins;
        updateSeries.player2Wins = player2Wins;
      })
    );
    console.log("🚀 ~ update series", update);
    return update;
  } else {
  }
}

async function createNewSeries(game: Game) {
  const series = {
    title: game.title,
    description: game.description,
    player1: game.player1,
    player2: game.player2,
    // games: [game],
    gameIds: [game.id],
  };
  const newSeries = await DataStore.save(new GameSeries(series));
  console.log("🚀 ~ newSeries", newSeries);
  return newSeries;
}

export async function addGameToLocalStorage(game?: Game) {
  if (!game) {
    return false;
  }
  console.log(`adding game ${game.id} to localStorage`);

  // Use "gameList" key for cloud sync compatibility
  const myGamesStr = localStorage.getItem("gameList");
  if (myGamesStr) {
    let myGamesIds = JSON.parse(myGamesStr);
    if (myGamesIds.indexOf(game.id) === -1) {
      myGamesIds.push(game.id);
      localStorage.setItem("gameList", JSON.stringify(myGamesIds));
    }
  } else {
    localStorage.setItem("gameList", JSON.stringify([game.id]));
  }

  // Sync to cloud if authenticated
  try {
    const { persistLocalStateToCloud } = await import("./playerState");
    await persistLocalStateToCloud();
    console.log("✅ Game synced to cloud");
  } catch (error) {
    console.log(
      "ℹ️ Could not sync to cloud (not authenticated or error):",
      error
    );
  }

  if (game.seriesId) {
    const mySeriesObj = await DataStore.query(GameSeries, game.seriesId);
    let mySeriesStr = localStorage.getItem("mySeries");
    if (mySeriesStr) {
      let mySeries: GameSeries[] = JSON.parse(mySeriesStr);
      if (mySeries.length > 0) {
        let mySeriesIds = mySeries
          .filter((s) => !!s && !!s.id)
          .map((s) => s!.id);
        if (mySeriesIds.indexOf(game.seriesId) === -1) {
          if (mySeriesObj) {
            mySeries.push(mySeriesObj);
            localStorage.setItem("mySeries", JSON.stringify(mySeries));
          }
        }
      }
    } else {
      localStorage.setItem("mySeries", JSON.stringify([mySeriesObj]));
    }
  }

  void persistLocalStateToCloud();
}

/**
 * Remove a game from localStorage and sync to cloud
 */
export async function removeGameFromLocalStorage(
  gameId: string
): Promise<boolean> {
  if (!gameId) {
    return false;
  }

  console.log(`🗑️ Removing game ${gameId} from localStorage`);

  try {
    // Use "gameList" key for cloud sync compatibility
    const myGamesStr = localStorage.getItem("gameList");
    if (!myGamesStr) {
      console.log("ℹ️ No games in localStorage");
      return true; // Nothing to remove
    }

    let myGamesIds: string[] = JSON.parse(myGamesStr);
    const initialLength = myGamesIds.length;

    // Remove the game ID
    myGamesIds = myGamesIds.filter((id) => id !== gameId);

    if (myGamesIds.length === initialLength) {
      console.log("ℹ️ Game not found in localStorage");
      return true; // Nothing was removed, but that's ok
    }

    // Save updated list
    localStorage.setItem("gameList", JSON.stringify(myGamesIds));
    console.log(
      `✅ Removed game from localStorage (${initialLength} -> ${myGamesIds.length})`
    );

    // Sync to cloud if authenticated
    try {
      const { persistLocalStateToCloud } = await import("./playerState");
      await persistLocalStateToCloud();
      console.log("✅ Game removal synced to cloud");
    } catch (error) {
      console.log(
        "ℹ️ Could not sync to cloud (not authenticated or error):",
        error
      );
    }

    return true;
  } catch (error) {
    console.error("❌ Failed to remove game from localStorage:", error);
    return false;
  }
}

// export function addGameToLocalStorage(gameId: string) {
//   console.log(`adding game ${gameId} to localStorage`);

//   const myGamesStr = localStorage.getItem("myGames");
//   if (myGamesStr) {
//     let myGames = JSON.parse(myGamesStr);
//     if (myGames.indexOf(gameId) === -1) {
//       myGames.push(gameId);
//       localStorage.setItem("myGames", JSON.stringify(myGames));
//     }
//   } else {
//     localStorage.setItem("myGames", JSON.stringify([gameId]));
//   }
// }

export function createNewBoard() {
  let board = [];
  for (let i = 0; i < 16; i++) {
    let row = "A";
    let col = (i % 4) + 1;
    if (i >= 4) row = "B";
    if (i >= 8) row = "C";
    if (i >= 12) row = "D";
    const newField = { name: `${row}${col}`, key: i };
    board.push(newField);
  }
  return board;
}

export async function createRematch(
  game: Game,
  navigate?: ReturnType<typeof useNavigate>
) {
  console.log("🚀 ~ common: createRematch");
  let rematch: Game | undefined;

  const gameStorage = JSON.parse(localStorage.getItem(game.id) || "{}");
  const playerNoInRematch = Number(gameStorage.player) === 1 ? 2 : 1;

  if (game.seriesId) {
    console.log("add game to series", game.seriesId);
    // updateSeries(game);
    const newGame = {
      title: game?.title!,
      description: game?.description!,
      seriesId: game.seriesId,
      player1: game.player2!,
      player2: game.player1!,
      currentPlayer: 1,
      expertMode: game.expertMode || false,
      rejoinedPlayers: [playerNoInRematch],
    };

    rematch = await createGame(newGame);
    console.log("rematch is: ", rematch);
    await updateSeries(rematch!);
    await updateGameInDB(game!.id, {
      rematchId: rematch?.id,
    });
  } else {
    const series = await createNewSeries(game);
    console.log("🚀 ~ series is ", series);
    const newGame = {
      title: game?.title!,
      description: game?.description!,
      seriesId: series.id,
      player1: game.player2!,
      player2: game.player1!,
      expertMode: game.expertMode || false,
      currentPlayer: 1,
      rejoinedPlayers: [playerNoInRematch],
    };

    rematch = await createGame(newGame);
    console.log("rematch is: ", rematch);
    await updateSeries(rematch!);
    await updateGameInDB(game!.id, {
      seriesId: series.id,
      rematchId: rematch?.id,
    });
  }

  if (rematch) {
    localStorage.setItem(
      rematch.id,
      JSON.stringify({ player: playerNoInRematch })
    );
    void persistLocalStateToCloud();
  }

  if (navigate && rematch) {
    navigate(`/game/${rematch.id}?rematch=true`);
    window.location.reload();
  }
}
export async function createGame(
  gameParams?: {
    count?: number;
    title?: string;
    description?: string;
    seriesId?: string;
    currentPlayer?: number;
    player1?: string;
    player2?: string;
    rejoinedPlayers?: number[];
    expertMode?: boolean;
  },
  navigate?: ReturnType<typeof useNavigate>
) {
  //   return async () => {
  const count = gameParams?.count || 0;
  const game = {
    title:
      gameParams?.title ||
      t("CreateGameDefaults.title", { count: (count || 0) + 1 }),
    description:
      gameParams?.description ||
      t("CreateGameDefaults.description", {
        count: (count || 0) + 1,
      }),
    seriesId: gameParams?.seriesId || null,
    player1: gameParams?.player1 || null,
    player2: gameParams?.player2 || null,
    currentPlayer: gameParams?.currentPlayer || null,
    rejoinedPlayers: gameParams?.rejoinedPlayers || null,
    expertMode: gameParams?.expertMode || false,
    board: createNewBoard(),
  };

  console.log("📝 [createGame] Creating game via API.graphql()...", game);

  // Check if user is logged in to set owner
  let owner = null;
  let sharedWith: string[] = [];

  try {
    const user = await Auth.currentAuthenticatedUser();
    owner = user.attributes.sub;
    sharedWith = [owner]; // Creator automatically has access
    console.log("✅ Creating game as logged in user:", owner);
  } catch {
    console.log("ℹ️ Creating anonymous game (no owner)");
  }

  try {
    // Use API.graphql() directly instead of DataStore.save()
    const response: any = await API.graphql({
      query: createGameMutation,
      variables: {
        input: {
          title: game.title,
          description: game.description,
          seriesId: game.seriesId,
          player1: game.player1,
          player2: game.player2,
          currentPlayer: game.currentPlayer,
          rejoinedPlayers: game.rejoinedPlayers,
          expertMode: game.expertMode,
          board: game.board,
          gameHasStarted: false,
          owner,
          sharedWith,
        },
      },
    });

    if (!response?.data?.createGame) {
      throw new Error("API returned no game data");
    }

    const newGame = response.data.createGame;
    console.log("✅✅✅ [createGame] Game created in DynamoDB via API!");
    console.log("   Game ID:", newGame.id);
    console.log("   Title:", newGame.title);
    console.log("   Version:", newGame._version);

    // Verify immediately that it's in DynamoDB
    console.log("🔍 [createGame] Verifying game exists in DynamoDB...");
    const verifyResponse: any = await API.graphql({
      query: getGame,
      variables: { id: newGame.id },
    });

    if (verifyResponse?.data?.getGame) {
      console.log("✅ [createGame] VERIFIED: Game IS in DynamoDB!");
    } else {
      console.warn("⚠️ [createGame] Could not verify game in DynamoDB");
    }

    // Add to appropriate tracking
    if (!owner) {
      // Anonymous: Add to localStorage tracking
      const { addAnonymousGame } = await import("./anonymousGames");
      addAnonymousGame(newGame.id, 1, game.player1 || "Player 1");
      console.log("✅ Added to anonymous games");
    }
    // If logged in: no need to track in localStorage, owner field is enough

    if (navigate) {
      console.log("📝 [createGame] Navigating to game:", newGame.id);
      navigate(`/game/${newGame.id}`, { replace: true });
    } else {
      return newGame;
    }
  } catch (error: any) {
    console.error("💥 [createGame] Failed to create game via API:", error);
    console.error("   Error details:", error.errors || error.message);
    if (navigate) {
      navigate("/error", { state: { message: t("CreateGame.createError") } });
    }
    throw error;
  }
}

export function dateSorter<T extends object>(
  property: keyof T,
  direction: "ASC" | "DESC"
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const valA = a[property];
    const valB = b[property];
    if (valA && valB) {
      if (direction === "DESC") {
        return valA > valB ? -1 : valA < valB ? 1 : 0;
      } else {
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      }
    } else {
      return 0;
    }
  };
}

export const isMyTurn = (game: Game) => {
  if (game.winRow || game.winner) {
    return false;
  }
  const item = localStorage.getItem(game.id);
  if (item) {
    // console.log("isMyTurn?");
    const gameInfo = JSON.parse(item);
    if (gameInfo) {
      const returnVal = Number(gameInfo.player) === game.currentPlayer;
      //   console.log("🚀 ~ gameInfo", gameInfo);
      //   console.log("🚀 ~ returnVal", returnVal);
      return returnVal;
    }
  }

  return false;
};

export const whoAmI = (game: Game) => {
  const item = localStorage.getItem(game.id);
  if (item) {
    const gameInfo = JSON.parse(item);
    if (gameInfo) {
      if (Number(gameInfo.player) === 1) return game.player1;
      if (Number(gameInfo.player) === 2) return game.player2;
    }
  }
  return "";
};

export const myOpponent = (game: Game) => {
  const item = localStorage.getItem(game.id);
  if (item) {
    const gameInfo = JSON.parse(item);
    if (gameInfo) {
      if (Number(gameInfo.player) === 1) return game.player2;
      if (Number(gameInfo.player) === 2) return game.player1;
    }
  }
  return "";
};
